import React, { useMemo, useState } from 'react';
import { Alert, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { EmailAddressSearch } from '../EmailAddress/EmailAddressSearch';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';

interface ChooseEmailButtonProps {
  clientId?: string;
  salesInvoiceId?: string;
  refresh: boolean;
  setRefresh: (refresh: boolean) => void;
}

export const ChooseEmailButton: React.FC<ChooseEmailButtonProps> = props => {
  const { t, tk } = useTranslation('fvsLng');
  const [show, setShow] = useState<boolean>(false);
  const [selectedEmailAddressId, setSelectedEmailAddressId] = useState<
    string | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const pok = usePokCore();

  const handleClick = () => {
    setShowSaveErrors(false);
    setSaveErrors([]);
    setShow(true);
  };

  const onRowClick = (cid: string) => {
    setSelectedEmailAddressId(cid);
  };

  const onSelectEmailAddress = () => {
    if (props.salesInvoiceId && selectedEmailAddressId) {
      setIsLoading(true);
      pok.salesInvoices
        .updateEmailAddress(props.salesInvoiceId, selectedEmailAddressId)
        .then(() => {
          handleClose();
          setShow(false);
        })
        .catch(async response => {
          const errors = await responseErrors(response as ResponseError);
          setShowSaveErrors(true);
          setSaveErrors(errors);
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleClose = () => {
    props.setRefresh(!props.refresh);
    setShow(false);
    setSelectedEmailAddressId(undefined);
  };

  const getData = useMemo(
    () =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) =>
        pok.emailAddresses.getAllByPortionForClient(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          props.clientId ? props.clientId : undefined,
          true,
        )) as GridGetterFunction,
    [pok.emailAddresses, props.clientId],
  );

  const getCount = useMemo(
    () =>
      (filterText =>
        pok.emailAddresses.getAllCountForClient(
          filterText,
          props.clientId ? props.clientId : undefined,
          true,
        )) as GridCountFunction,
    [pok.emailAddresses, props.clientId],
  );

  return (
    <>
      <OverlayTrigger
        overlay={<Tooltip>{t(tk.sendEmail.chooseEmailAddress)}</Tooltip>}
      >
        <Button
          onClick={e => {
            e.stopPropagation();
            handleClick();
          }}
          variant="btn-info"
          className="px-2"
        >
          <Icon.PersonFillAdd className="icon-green" size={18} />
        </Button>
      </OverlayTrigger>
      {show && (
        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
        <div onClick={e => e.stopPropagation()}>
          <Modal
            show={show}
            onHide={handleClose}
            centered
            keyboard={true}
            backdrop="static"
            backdropClassName="modal-on-modal-backdrop"
            className="modal-on-modal"
            dialogClassName="modal-90w"
          >
            <Modal.Header closeButton>
              <Modal.Title>{t(tk.sendEmail.chooseEmailAddress)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <EmailAddressSearch
                getData={getData}
                getCount={getCount}
                onRowClick={onRowClick}
                refresh={props.refresh}
                enableSingleSelect={true}
                selectedKeys={[selectedEmailAddressId || '']}
                withHistory={false}
              />
              <Alert variant="info" className="m-3">
                {t(tk.sendEmail.warningSelectEmailAddress)}
              </Alert>
              <ValidationAlert
                show={showSaveErrors}
                errors={saveErrors}
                className="m-3"
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleClose}>
                {t(tk.button.close)}
              </Button>
              <Button
                variant="primary"
                onClick={onSelectEmailAddress}
                isLoading={isLoading}
              >
                {t(tk.button.select)}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </>
  );
};
